import { QueryKey } from '@tanstack/react-query';
import queryClient from './query-client';
import { useQueryWithCallback } from './use-query-with-callback';

/**
 * Map of query keys use by the react-query hooks.
 */
export const queryKeys = {
    allWorkflowInstances: 'allWorkflowInstances',
    accountSync: 'accountSync',
    checkinEntries: 'checkinEntries',
    checkinEntriesLatest: 'checkinEntriesLatest',
    circles: 'circles',
    circleById: 'circleById',
    countries: 'countries',
    dependentPartnerById: 'dependentPartnerById',
    deviceConsent: 'deviceConsent',
    recommendations: 'recommendations',
    referrals: 'referrals',
    workflowByDefnId: 'workflowByDefnId',
    workflowByType: 'workflowByType',
    workflowNextStepByDefnId: 'workflowNextStepByDefnId',
    packsByPackClass: 'packsByPackClass',
    recipientPrograms: 'recipientPrograms',
    forYouRecommendations: 'forYouRecommendations',
    clinicalPacksByPackClass: 'clinicalPacksByPackClasses',
    mediaCookies: 'mediaCookies',
    conditions: 'conditions',
    dailies: 'dailies',
    dailiesIndexes: 'dailiesIndexes',
    sleepStories: 'sleepStories',
    discoverSections: 'discoverSections',
    genders: 'genders',
    goals: 'goals',
    guide: 'guide',
    invitations: 'invitations',
    invitationByCode: 'invitationByCode',
    invitationById: 'invitationById',
    mostRecentSponsorship: 'mostRecentSponsorship',
    needsRegistrationConsent: 'needsRegistrationConsent',
    notificationPreferences: 'notificationPreferences',
    notificationUser: 'notificationUser',
    poll: 'poll',
    pollSubmission: 'pollSubmission',
    productsByUserId: 'userProducts',
    programs: 'programs',
    publicNarrators: 'publicNarrators',
    publicProgram: 'publicProgram',
    publicBrandingPartnerConfig: 'publicBrandingPartnerConfig',
    partnerByCode: 'partnerByCode',
    partnerById: 'partnerById',
    partnerEligibility: 'partnerEligibility',
    refreshAccessToken: 'refreshAccessToken',
    recentAndFavoriteGuides: 'recentAndFavoriteGuides',
    resources: 'resources',
    tosCohortConsent: 'tosCohortConsent',
    tokenData: 'tokenData',
    videoBackground: 'videoBackground',
    healthSummary: 'healthSummary',
    healthSummarySignedUrl: 'healthSummarySignedUrl',
    previousRegistrationFlow: 'previousRegistrationFlow',
    unsponsoredConfig: 'unsponsoredConfig',
    search: 'search',
    searchTrending: 'searchTrending',
    searchTypeahead: 'searchTypeahead',
    searchResults: 'searchResults',
};

export * from '@tanstack/react-query';

export const invalidateQueryKey = (key: QueryKey) => {
    return queryClient.invalidateQueries({ queryKey: key });
};

export interface ItemToQueryMapping<T> {
    item: T;
    queryKey: QueryKey;
}
interface CachedQuery<T> {
    queryKey: QueryKey;
    data: T;
}

interface QueryCacheCheckResult<ItemType, DataType> {
    cachedQueries: CachedQuery<DataType>[];
    invalidQueries: ItemToQueryMapping<ItemType>[];
}

/**
 * Checks the query cache for existing data and determines which queries need fetching
 *
 * This function examines each provided query key to determine if its corresponding data
 * exists in the cache and is still fresh (not stale). It categorizes queries into two groups:
 * - cachedQueries: Queries that have valid, non-stale data in the cache
 * - invalidQueries: Queries that either don't exist in the cache or have stale data
 *
 * This allows components to optimize data fetching by only requesting data that isn't
 * already available in the cache, while still using cached data that's still fresh.
 *
 * @template ItemType - The type of item being mapped to a query key (e.g., string for pack classes)
 * @template DataType - The expected data type returned by the query (e.g., MediaPack)
 *
 * @param keyObjects - An array of objects mapping items to their query keys
 * @param staleTime - The duration in milliseconds before cached data is considered stale
 *
 * @returns An object containing both cached queries (with their data) and invalid queries
 * that need to be fetched
 */
export function checkQueryCache<ItemType, DataType>(
    keyObjects: ItemToQueryMapping<ItemType>[],
    staleTime: number
): QueryCacheCheckResult<ItemType, DataType> {
    return keyObjects.reduce<QueryCacheCheckResult<ItemType, DataType>>(
        ({ cachedQueries, invalidQueries }, keyObj) => {
            const { queryKey } = keyObj;
            const queryState = queryClient.getQueryState<DataType>(queryKey);
            const cachedItem = queryState?.data;

            // If query exists and is not stale, use the cached data
            if (
                cachedItem &&
                queryState &&
                !queryState.isInvalidated &&
                queryState.dataUpdatedAt &&
                queryState.dataUpdatedAt > Date.now() - staleTime
            ) {
                cachedQueries.push({
                    queryKey,
                    data: cachedItem,
                });
            } else {
                invalidQueries.push(keyObj);
            }
            return { cachedQueries, invalidQueries };
        },
        { cachedQueries: [], invalidQueries: [] }
    );
}

export { queryClient, useQueryWithCallback };
